import React from "react";
import { BrowserRouter, Routes, Route, Link, Navigate } from "react-router-dom";
import { Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Analytics } from "@vercel/analytics/react";
import { SimpleProviders } from "@core/components/SimpleProviders";
import { AuthRequired } from "@core/components/AuthRequired";
import { ProductHeader } from "@core/components/layout/products/ProductHeader";
import { ProductSidebar } from "@core/components/layout/products/ProductSidebar";
import AccountSubscription from "@core/pages/account/AccountSubscription";
import AccountInvoices from "@core/pages/account/AccountInvoices";
import AccountSecurity from "@core/pages/account/AccountSecurity";
import AccountPreferences from "@core/pages/account/AccountPreferences";
import AccountApiKeys from "@core/pages/account/AccountApiKeys";

import { AdminGuard } from "./components/admin/AdminGuard";

import FormPollsLanding from "./pages/LandingPage";
import FormPollsAbout from "./pages/About";
import FormCreator from "./pages/FormCreator";
import FormPollsDashboard from "./pages/Dashboard";
import Vote from "./pages/Vote";
import Results from "./pages/Results";
import EmbedVote from "./pages/EmbedVote";
import EmbedResults from "./pages/EmbedResults";
import FormLegal from "./pages/Legal";
import Pricing from "./pages/Pricing";
import Documentation from "./pages/Documentation";
import DataControl from "./pages/DataControl";
import AdminLoginPage from "./pages/AdminLoginPage";
import AuthCallback from "./pages/AuthCallback";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import { SupportPage } from "./pages/SupportPage";
import { SupportFeedbackPage } from "./pages/SupportFeedbackPage";
import GuidesIndex from "./pages/GuidesIndex";
import GuidePage from "./pages/GuidePage";
import JournalSimple from "./pages/JournalSimple";
import DocsPage from "./pages/DocsPage";
import GroupsPage from "./pages/GroupsPage";
import { RoadmapPage } from "../../../src/app/roadmap/RoadmapPage";
import { FeatureRequestDetailPage } from "../../../src/app/roadmap/[id]/FeatureRequestDetailPage";
import { UsersAdmin } from "./pages/admin/UsersAdmin";
import { SupportDashboard } from "@core/components/admin/support/SupportDashboard";
import { FormMetricsDashboard } from "./pages/admin/FormMetricsDashboard";
import { FormPulseDashboard } from "./pages/admin/FormPulseDashboard";
import { usePageTitle } from "./hooks/usePageTitle";

const AccountLayout = ({ children }: { children: React.ReactNode }) => (
  <AuthRequired>
    <div className="min-h-screen-safe bg-gray-950">
      <ProductHeader productType="form" showAuth={true} />
      <div className="flex">
        <ProductSidebar productType="form" />
        <div className="flex-1 min-w-0">{children}</div>
      </div>
    </div>
  </AuthRequired>
);

const AdminShell = ({ children }: { children: React.ReactNode }) => (
  <div className="min-h-screen-safe bg-[#030712] text-white">
    <header className="relative z-10 border-b border-white/5 bg-black/20 backdrop-blur-md">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="flex items-center gap-3 cursor-pointer">
            <div className="w-8 h-8 rounded bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <span className="text-md font-semibold text-white">
              DooQuest Admin
            </span>
          </Link>
          <nav className="flex items-center gap-4">
            <NavMetricsLink />
            <Link
              to="/admin/pulse"
              className="text-xs text-gray-400 hover:text-white transition-colors"
            >
              Pulse
            </Link>
            <Link
              to="/admin/support"
              className="text-xs text-gray-400 hover:text-white transition-colors"
            >
              Support
            </Link>
            <Link
              to="/admin/users"
              className="text-xs text-gray-400 hover:text-white transition-colors"
            >
              Users
            </Link>
            <NavLogoutLink />
          </nav>
        </div>
      </div>
    </header>
    <main>{children}</main>
  </div>
);

function NavMetricsLink() {
  const { t } = useTranslation("forms");
  return (
    <Link
      to="/admin"
      className="text-xs text-gray-400 hover:text-white transition-colors"
    >
      {t("nav.metrics")}
    </Link>
  );
}

function NavLogoutLink() {
  const { t } = useTranslation("forms");
  return (
    <Link
      to="/"
      className="text-xs text-gray-500 hover:text-white transition-colors"
    >
      {t("admin.logout")}
    </Link>
  );
}

function NotFoundPage() {
  const { t } = useTranslation("forms");
  return (
    <div>
      <h1 className="text-2xl font-bold text-red-600">{t("page_404.title")}</h1>
    </div>
  );
}

function PageNotFound() {
  const { t } = useTranslation("forms");
  return (
    <div className="p-8 text-center">
      <h1 className="text-4xl font-bold text-gray-600 mb-4">
        {t("page_404.heading")}
      </h1>
      <p className="text-gray-500 mb-6">{t("page_404.message")}</p>
      <a
        href="/form/"
        className="inline-block px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
      >
        {t("admin.return_home")}
      </a>
    </div>
  );
}

export default function App() {
  usePageTitle();

  return (
    <BrowserRouter
      basename={window.location.pathname.startsWith("/form") ? "/form" : "/"}
    >
      <SimpleProviders>
        <Analytics />
        <Routes>
          <Route path="/" element={<FormPollsLanding />} />
          <Route path="/about" element={<FormPollsAbout />} />
          <Route path="/create" element={<FormCreator />} />
          {/* Alias édition : dashboard PollCard navigue /workspace/form?edit=<id>.
              Même composant que /create (lit ?edit= via AICreationWorkspace). */}
          <Route path="/workspace/form" element={<FormCreator />} />
          <Route path="/workspace/date" element={<FormCreator />} />
          <Route path="/workspace/availability" element={<FormCreator />} />
          <Route path="/dashboard" element={<FormPollsDashboard />} />
          <Route path="/tableau-de-bord" element={<FormPollsDashboard />} />
          <Route path="/poll/:slug" element={<Vote />} />
          <Route path="/poll/:slug/vote" element={<Vote />} />
          <Route path="/poll/:slug/results" element={<Results />} />
          {/* Vues embed nues (iframe tierce) — framing autorisé via vercel.json */}
          <Route path="/embed/:slug" element={<EmbedVote />} />
          <Route path="/embed/:slug/results" element={<EmbedResults />} />
          <Route path="/data-control" element={<DataControl />} />

          {/* Documentation et Pricing */}
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/docs" element={<Documentation />} />
          <Route path="/docs/page/:docPath" element={<DocsPage />} />
          {/* Guides : /guide (ancien UserGuide) fusionné dans /guides/prise-en-main.
              GuidePage gère la résolution de slug par langue (slugs localisés). */}
          <Route path="/guides" element={<GuidesIndex />} />
          <Route path="/guides/:slug" element={<GuidePage />} />
          <Route
            path="/guide"
            element={<Navigate to="/guides/prise-en-main" replace />}
          />
          <Route path="/groupes" element={<GroupsPage />} />
          <Route path="/journal" element={<JournalSimple />} />

          {/* Roadmap publique + vote (composant partagé @core, thème form) */}
          <Route path="/roadmap" element={<RoadmapPage productType="form" />} />
          <Route path="/roadmap/:id" element={<FeatureRequestDetailPage />} />

          {/* Légal */}
          <Route path="/terms" element={<FormLegal />} />
          <Route path="/privacy" element={<FormLegal />} />
          <Route path="/legal" element={<FormLegal />} />
          <Route path="/docs/:docPath" element={<FormLegal />} />

          {/* Support */}
          <Route path="/support" element={<SupportPage />} />
          <Route
            path="/support/feedback/:ticketId"
            element={<SupportFeedbackPage />}
          />

          {/* Auth */}
          <Route path="/login" element={<AdminLoginPage />} />
          <Route path="/auth/signin" element={<AdminLoginPage />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route
            path="/auth/forgot-password"
            element={<ForgotPasswordPage />}
          />
          <Route path="/auth/reset-password" element={<ResetPasswordPage />} />

          {/* Account — protégé par auth + layout */}
          <Route
            path="/account/overview"
            element={<Navigate to="/account/subscription" replace />}
          />
          <Route
            path="/account/subscription"
            element={
              <AccountLayout>
                <AccountSubscription />
              </AccountLayout>
            }
          />
          <Route
            path="/account/invoices"
            element={
              <AccountLayout>
                <AccountInvoices />
              </AccountLayout>
            }
          />
          <Route
            path="/account/security"
            element={
              <AccountLayout>
                <AccountSecurity />
              </AccountLayout>
            }
          />
          <Route
            path="/account/preferences"
            element={
              <AccountLayout>
                <AccountPreferences />
              </AccountLayout>
            }
          />
          <Route
            path="/account/api-keys"
            element={
              <AccountLayout>
                <AccountApiKeys />
              </AccountLayout>
            }
          />

          {/* Admin — gated par AdminGuard.
              Métriques = vue scopée form (≠ Pulse global, retiré du form admin).
              Tickets support sur /admin/support (table partagée). */}
          <Route
            path="/admin"
            element={
              <AdminGuard>
                <AdminShell>
                  <FormMetricsDashboard />
                </AdminShell>
              </AdminGuard>
            }
          />
          <Route
            path="/admin/pulse"
            element={
              <AdminGuard>
                <AdminShell>
                  <FormPulseDashboard />
                </AdminShell>
              </AdminGuard>
            }
          />
          <Route
            path="/admin/support"
            element={
              <AdminGuard>
                <AdminShell>
                  <SupportDashboard />
                </AdminShell>
              </AdminGuard>
            }
          />
          <Route
            path="/admin/users"
            element={
              <AdminGuard>
                <AdminShell>
                  <UsersAdmin />
                </AdminShell>
              </AdminGuard>
            }
          />

          <Route
            path="/poll/:pollId/delete"
            element={
              <div className="p-8 text-center" data-testid="access-denied">
                <NotFoundPage />
              </div>
            }
          />

          <Route
            path="*"
            element={
              <div className="p-8 text-center" data-testid="form-polls-404">
                <PageNotFound />
              </div>
            }
          />
        </Routes>
      </SimpleProviders>
    </BrowserRouter>
  );
}
